import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";
import { CountTriple, DetailRow, PercentBarList } from "./SupplierManagementPrimitives";
import {
  SUPPLIER_STATUS,
  detailSectionStyle,
  detailSectionTitle,
} from "./supplierManagementStyles";

/**
 * Loads and renders the full supplier detail block for the given
 * supplier id. Owns its own loading state so the parent page doesn't
 * have to manage the fetch lifecycle.
 */
const SupplierDetailsModal = ({ show, supplierId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!show || !supplierId) {
      setDetails(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDetails(null);
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}suppliers/${supplierId}`,
        {},
        {},
        "GET"
      );
      if (cancelled) return;
      setLoading(false);
      if (isSuccess) {
        setDetails(data?.data ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, supplierId]);

  const renderBody = () => {
    if (loading) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: 48,
          }}
        >
          <div className="kpi-spinner" />
          <span style={{ fontSize: 13, color: "var(--ink-4)", fontWeight: 600 }}>
            Loading details…
          </span>
        </div>
      );
    }
    if (!details) {
      return (
        <div style={{ padding: 32, textAlign: "center", color: "var(--ink-4)" }}>
          No details available.
        </div>
      );
    }
    const s = details?.supplier || {};
    const p = s?.profile || {};
    const fullName =
      [s?.first_name, s?.last_name].filter(Boolean).join(" ") || "—";
    const isActive = details?.status === SUPPLIER_STATUS.ACTIVE;
    const perm = p?.permanent_employees || {};
    const nonPerm = p?.non_permanent_employees || {};
    const plants = p?.manufacturing_plants || {};
    const offices = p?.offices || {};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div
          style={{
            ...detailSectionStyle,
            background: "var(--surface)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: "var(--ink)",
                lineHeight: 1.2,
              }}
            >
              {s?.company_name || "—"}
            </div>
            {p?.entity_type ? (
              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--ink-3)",
                  fontFamily: "var(--font-mono)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginTop: 4,
                }}
              >
                {p.entity_type.replace(/_/g, " ")}
              </div>
            ) : null}
          </div>
          <span
            style={{
              display: "inline-block",
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontFamily: "var(--font-mono)",
              background: isActive ? "#dcfce7" : "#fee2e2",
              color: isActive ? "#15803d" : "#b91c1c",
              border: `1px solid ${isActive ? "#86efac" : "#fecaca"}`,
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>

        <div style={detailSectionStyle}>
          <div style={detailSectionTitle}>Overview</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            <DetailRow label="Industry" value={s?.industry_name ?? p?.industry_name} />
            <DetailRow
              label="Sub-industry"
              value={s?.sub_industry_name ?? p?.sub_industry_name}
            />
            <DetailRow label="Employee size" value={p?.employee_size_category} />
            <DetailRow label="Country" value={s?.country} />
          </div>
          <div style={{ marginTop: 12 }}>
            <DetailRow label="Registered address" value={p?.registered_address} />
          </div>
        </div>

        <div style={detailSectionStyle}>
          <div style={detailSectionTitle}>Primary contact</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            <DetailRow label="Name" value={fullName} />
            <DetailRow label="Email" value={s?.email} />
            <DetailRow label="Mobile" value={s?.mobile_number} />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
          }}
        >
          <div style={detailSectionStyle}>
            <div style={detailSectionTitle}>Business activities</div>
            <PercentBarList items={p?.business_activities} />
          </div>
          <div style={detailSectionStyle}>
            <div style={detailSectionTitle}>Products & services</div>
            <PercentBarList items={p?.products_or_services} />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          <div style={detailSectionStyle}>
            <div style={detailSectionTitle}>Manufacturing plants</div>
            <div style={{ display: "flex", gap: 8 }}>
              <CountTriple label="National" value={plants?.national} />
              <CountTriple label="International" value={plants?.international} />
            </div>
          </div>
          <div style={detailSectionStyle}>
            <div style={detailSectionTitle}>Offices</div>
            <div style={{ display: "flex", gap: 8 }}>
              <CountTriple label="National" value={offices?.national} />
              <CountTriple label="International" value={offices?.international} />
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
          }}
        >
          <div style={detailSectionStyle}>
            <div style={detailSectionTitle}>Permanent employees</div>
            <div style={{ display: "flex", gap: 8 }}>
              <CountTriple label="Male" value={perm?.male} />
              <CountTriple label="Female" value={perm?.female} />
              <CountTriple label="Others" value={perm?.others} />
            </div>
          </div>
          <div style={detailSectionStyle}>
            <div style={detailSectionTitle}>Non-permanent employees</div>
            <div style={{ display: "flex", gap: 8 }}>
              <CountTriple label="Male" value={nonPerm?.male} />
              <CountTriple label="Female" value={nonPerm?.female} />
              <CountTriple label="Others" value={nonPerm?.others} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable>
      <Modal.Header closeButton style={{ borderBottom: "1px solid var(--rule)" }}>
        <Modal.Title style={{ fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>
          Supplier details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#f0f2f5" }}>{renderBody()}</Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid var(--rule)", gap: 8 }}>
        <button type="button" className="kpi-btn-secondary" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default SupplierDetailsModal;
